/**
 * Chinese (Simplified) dictionary — canonical source for the tenant
 * console-web key space.
 *
 * `en.ts` re-uses this dictionary's type (`typeof zhCN`) so every key
 * that exists here must exist there. Adding a new string: add the key
 * to this file first, then add the English translation. Removing a
 * string: delete from both files and also sweep call sites for the key.
 *
 * Conventions
 * - Keys are lowercase dot-paths: `feature.subfeature.detail`.
 * - Templates use `{name}` placeholders; the `t()` formatter substitutes
 *   on render. Unknown placeholders render literally so missing params
 *   fail loud in QA rather than silently eat text.
 * - Punctuation: Chinese sentences end in `。`, lists use `、`; follow
 *   the surrounding copy for spacing. English sentences follow a normal
 *   period. Do not include trailing whitespace.
 */
export const zhCN = {
  // ── <head> metadata ─────────────────────────────────────────────
  // Synced to `document.title` and `<meta name="description">` at
  // runtime by `I18nProvider`. The static value in `index.html`
  // is the zh-CN first-paint fallback; an en-preferring visitor's
  // provider effect rewrites both on mount.
  'meta.title': '哪都通控制台',
  'meta.description': '哪都通 API 控制台：管理 API 密钥、查看使用情况、充值余额并邀请团队成员。',

  // ── Common / shared ──────────────────────────────────────────────
  'common.brand.title': '哪都通控制台',
  'common.session.checking': '正在检查会话…',
  'common.back_to_login': '返回登录',
  'common.technical_details.title': '技术信息',
  'common.technical_details.show': '展开',
  'common.technical_details.hide': '收起',

  // Locale-switcher native labels live in `types.ts` as
  // `LOCALE_NATIVE_LABELS` — they are UI chrome (the name of each
  // language in its own script) rather than translatable content.

  // ── Login ───────────────────────────────────────────────────────
  'login.eyebrow': '登录',
  'login.subtitle': '登录你的哪都通账号',
  'login.description': '登录后即可管理 API 密钥、余额、使用情况和团队成员。',
  'login.email': '邮箱',
  'login.password': '密码',
  'login.submit': '登录',
  'login.submitting': '登录中…',
  'login.need_account': '还没有账号？立即注册',
  'login.forgot': '忘记密码？',
  'login.error.empty_fields': '请填写邮箱和密码',

  // ── Register ────────────────────────────────────────────────────
  'register.eyebrow': '创建账号',
  'register.subtitle': '创建哪都通账号',
  'register.description': '先创建你的账号，后续可以邀请团队成员一起使用。',
  'register.email': '邮箱',
  'register.password_with_hint': '密码（10–128 位）',
  'register.workspace_name': '团队名称（可选）',
  'register.workspace_placeholder': '例如：支付服务 / 数据平台 / 我的团队',
  'register.submit': '创建账号',
  'register.submitting': '创建中…',
  'register.error.empty_fields': '请填写邮箱和密码',
  'register.error.password_too_short': '密码长度至少 10 位',
  'register.success.title': '请查收邮件',
  'register.success.body':
    '我们已将验证链接发送至 {email}。打开邮件里的链接后，就可以继续进入控制台。',
  'register.success.resend_ack':
    '新的验证链接已发送。若仍未收到，请检查垃圾邮件，或稍等一分钟后再试。',
  'register.success.resend_hint': '如果暂时没收到，可以重新发送一次。',
  'register.resend.submit': '重发链接',
  'register.resend.submitting': '发送中…',
  'register.already_have_account': '已有账号?立即登录',

  // ── Forgot password ─────────────────────────────────────────────
  'forgot.eyebrow': '找回密码',
  'forgot.subtitle': '找回登录密码',
  'forgot.description': '输入注册邮箱，我们会把重置链接发送给你。',
  'forgot.email': '邮箱',
  'forgot.submit': '发送重置链接',
  'forgot.submitting': '发送中…',
  'forgot.error.empty_email': '请填写邮箱',
  'forgot.success.title': '请查收邮件',
  'forgot.success.body': '若 {email} 存在对应账号,我们已发送重置链接。链接 24 小时内有效。',

  // ── Reset password ──────────────────────────────────────────────
  'reset.eyebrow': '设置新密码',
  'reset.subtitle': '重新设置密码',
  'reset.description': '保存后就可以用新密码重新登录。',
  'reset.new_password': '新密码(10–128 位)',
  'reset.confirm_password': '确认新密码',
  'reset.submit': '保存新密码',
  'reset.submitting': '保存中…',
  'reset.error.missing_token': '重置链接中缺少 token',
  'reset.error.password_too_short': '密码长度至少 10 位',
  'reset.error.password_mismatch': '两次输入的密码不一致',
  'reset.error.token_banner': '缺少重置 token,请直接点击邮件中的链接,或重新申请一个。',
  'reset.success.title': '密码已更新',
  'reset.success.body': '密码已更新,请用新密码登录。',
  'reset.success.sign_in': '去登录',

  // ── Verify email ────────────────────────────────────────────────
  'verify.eyebrow': '验证邮箱',
  'verify.subtitle': '完成邮箱验证',
  'verify.description': '验证成功后会自动进入控制台。',
  'verify.missing_token': '此链接缺少验证 token,请直接打开邮件中的链接,或回到登录页重新申请。',
  'verify.pending': '正在验证邮箱…',
  'verify.error.title': '验证失败',
  'verify.resend.label': '申请新的验证链接',
  'verify.resend.submit': '发送新链接',
  'verify.resend.submitting': '发送中…',
  'verify.resend.ack.title': '请查收邮件',
  'verify.resend.ack.body': '若该邮箱账号尚未验证,新的验证链接已在发送途中。',

  // ── Invitation ──────────────────────────────────────────────────
  'invite.eyebrow': '团队邀请',
  'invite.subtitle': '加入团队',
  'invite.description': '先确认你要加入的团队，再继续完成加入流程。',
  'invite.loading': '正在加载邀请…',
  'invite.error.missing_token': 'URL 中缺少邀请 token',
  'invite.error.title': '邀请不可用',
  'invite.invite_to': '邀请你加入',
  'invite.role': '角色',
  'invite.invitee_email': '受邀邮箱',
  'invite.invited_by': '邀请者',
  'invite.expires': '过期时间',
  'invite.password_with_hint': '密码(10–128 位)',
  'invite.display_name': '显示名称(可选)',
  'invite.display_name_placeholder': '默认使用邮箱前缀',
  'invite.session_match': '你正以 {email} 登录。点击接受即可加入 {org}。',
  'invite.session_mismatch.body':
    '当前以 {current} 登录,但本邀请指向 {target}。输入该账号密码以接受邀请,或',
  'invite.session_mismatch.switch_link': '切换账号',
  'invite.session_mismatch.suffix': '。',
  'invite.accept': '接受邀请',
  'invite.join': '加入团队',
  'invite.accepting': '接受中…',
  'invite.cancel': '取消',
  'invite.error.password_too_short': '密码长度至少 {min} 位',
  'invite.error.password_too_long': '密码长度最多 {max} 位',
  'invite.toast.accepted': '邀请已接受',

  // ── Not found ──────────────────────────────────────────────────
  'notfound.title': '页面未找到',
  'notfound.body': '此控制台中不存在你访问的页面。',
  'notfound.cta': '返回仪表盘',

  // ── Shell chrome ───────────────────────────────────────────────
  'shell.brand.subtitle': 'API 控制台',
  'shell.brand.supporting': '在一个地方管理你的密钥、余额、使用情况和团队成员。',
  'shell.nav.aria_label': '主导航',
  'shell.nav.dashboard': '总览',
  'shell.nav.keys': '密钥',
  'shell.nav.usage': '使用情况',
  'shell.nav.requests': '请求日志',
  'shell.nav.pricing': '模型定价',
  'shell.nav.billing': '余额与账单',
  'shell.nav.settings': '账号与团队',
  'shell.org_chip.title': '当前团队',
  'shell.page.console_fallback': '控制台',
  'shell.session.active': '当前会话',
  'shell.session.verified': '邮箱已验证',
  'shell.session.unverified': '邮箱待验证',
  'shell.session.signed_in': '已登录',
  'shell.logout': '退出登录',

  // ── Dashboard ──────────────────────────────────────────────────
  'dashboard.title': '总览',
  'dashboard.subtitle': '快速查看余额、使用情况和最近变化。',
  'dashboard.stat.balance.label': '可用余额',
  'dashboard.stat.balance.updated': '更新于 {timestamp}',
  'dashboard.stat.balance.unavailable': '余额加载失败',
  'dashboard.stat.balance.loading': '加载中…',
  'dashboard.stat.balance.zero_cta': '余额为 0，去充值',
  'dashboard.stat.cost.label': '本月费用',
  'dashboard.stat.cost.tokens': '本月累计消耗 {tokens} Token',
  'dashboard.stat.cost.unavailable': '用量加载失败',
  'dashboard.stat.cost.loading': '加载中…',
  'dashboard.stat.keys.label': '可用密钥',
  'dashboard.stat.keys.description': '当前仍可继续使用的密钥数量',
  'dashboard.stat.keys.unavailable': '密钥加载失败',
  'dashboard.stat.keys.empty_cta': '还没有可用密钥，去创建一个',
  'dashboard.stat.last_request.label': '最近一次请求',
  'dashboard.stat.last_request.empty': '还没有请求记录',
  'dashboard.stat.last_request.hint': '最近有新请求时会自动更新',
  'dashboard.stat.last_request.empty_cta': '创建密钥后就可以开始调用',
  'dashboard.activity.title': '最近请求',
  'dashboard.activity.view_all': '查看全部用量',
  'dashboard.activity.loading': '正在加载最近活动…',
  'dashboard.activity.error': '无法加载最近活动,请重新加载重试。',
  'dashboard.activity.empty': '最近 7 天还没有请求记录。',
  'dashboard.activity.empty_cta': '先创建密钥，再让应用发起第一次调用',
  'dashboard.activity.column.hour': '小时',
  'dashboard.activity.column.requests': '请求数',
  'dashboard.activity.column.tokens': 'Token 用量',
  'dashboard.activity.column.cost': '费用',
  'dashboard.top_keys.title': '本月使用较多的密钥',
  'dashboard.top_keys.manage': '管理密钥',
  'dashboard.top_keys.loading': '正在加载密钥用量…',
  'dashboard.top_keys.error': '无法加载密钥用量。',
  'dashboard.top_keys.empty.chip': '暂无用量',
  'dashboard.top_keys.empty.hint': '开始调用后，这里会显示本月使用较多的密钥。',
  'dashboard.top_keys.empty_cta': '去创建密钥',
  'dashboard.top_keys.column.key': '密钥',
  'dashboard.top_keys.column.requests': '请求数',
  'dashboard.top_keys.column.cost': '费用',

  // ── API keys page ──────────────────────────────────────────────
  'keys.title': 'API 密钥',
  'keys.subtitle': '为你的应用创建和管理调用密钥。',
  'keys.create': '创建密钥',
  'keys.base_url.title': '接入地址',
  'keys.base_url.description': '把这个地址填到你的 SDK、客户端或服务端配置里。',
  'keys.loading': '正在加载密钥…',
  'keys.error': '无法加载密钥',
  'keys.empty': '暂无密钥,创建一个即可开始发起请求。',
  'keys.copied': '已复制',
  'keys.click_to_copy': '点击复制',
  'keys.show_revoked': '显示已吊销 ({count})',
  'keys.column.prefix': '前缀',
  'keys.column.name': '名称',
  'keys.column.vendor': '厂商',
  'keys.column.channel': '渠道',
  'keys.column.rate': '倍率',
  'keys.column.created': '创建时间',
  'keys.column.last_used': '最近使用',
  'keys.column.status': '状态',
  'keys.column.actions': '操作',
  'keys.status.revoked': '已停用',
  'keys.status.active': '可用',
  'keys.never_used': '从未',
  'keys.save': '保存',
  'keys.cancel': '取消',
  'keys.rename': '重命名',
  'keys.revoke': '删除',
  'keys.row_error.name_required': '名称不能为空',
  'keys.row_error.network': '网络错误',
  'keys.create_modal.title': '创建 API 密钥',
  'keys.create_modal.description':
    '给这把密钥起一个容易识别的名字。创建后只会显示一次完整密钥，请及时保存。',
  'keys.create_modal.name_label': '密钥名称(可选)',
  'keys.create_modal.name_placeholder': 'production-backend',
  'keys.create_modal.routing_summary': '创建密钥必须选择厂商和渠道，用于路由、可用模型和计费倍率。',
  'keys.create_modal.vendor_label': '厂商(必选)',
  'keys.create_modal.vendor_placeholder': '请选择厂商',
  'keys.create_modal.channel_label': '渠道(必选)',
  'keys.create_modal.channel_placeholder': '请选择渠道',
  'keys.create_modal.channel_required': '请选择厂商和渠道后再创建密钥。',
  'keys.create_modal.channels_error': '无法加载可用渠道，请稍后重试。',
  'keys.create_modal.channels_empty': '暂无可用渠道，请联系支持处理。',
  'keys.create_modal.catalog_error':
    '无法加载模型覆盖信息。仍可创建密钥，但创建后请用 /v1/models 确认可用模型。',
  'keys.create_modal.catalog_empty': '此渠道当前没有已发布的模型覆盖。',
  'keys.create_modal.submit': '创建密钥',
  'keys.create_modal.submitting': '创建中…',
  'keys.create_modal.cancel': '取消',
  'keys.technical.summary': '查看这把密钥绑定的技术通道与计费信息。',
  'keys.technical.vendor': '提供方',
  'keys.technical.channel': '技术通道',
  'keys.technical.rate': '计费倍率',
  'keys.technical.default': '默认',
  'keys.plaintext.title': '复制你的 API 密钥',
  'keys.plaintext.warning':
    '这是你唯一一次看到完整密钥。请立即复制并妥善保存；如果遗失，只能删除后重新创建。',
  'keys.plaintext.hint.click_to_copy': '点击复制',
  'keys.plaintext.hint.click_with_name': '{name} · 点击复制',
  'keys.plaintext.copied': '已复制到剪贴板',
  'keys.plaintext.copy_anywhere': '点击此区块任意位置复制',
  'keys.plaintext.done': '我已复制密钥',
  'keys.plaintext.copy_aria_label': '复制 API 密钥到剪贴板',
  'keys.models.count': '{count} 个模型',
  'keys.revoke_modal.title': '删除这把 API 密钥？',
  'keys.revoke_modal.description.before': '删除后，密钥 ',
  'keys.revoke_modal.description.after':
    ' 会立即失效；仍在使用它的应用会开始收到 401 错误。此操作不可撤销。',
  'keys.revoke_modal.submit': '删除',
  'keys.revoke_modal.submitting': '删除中…',
  'keys.revoke_modal.cancel': '取消',

  // ── Usage page ─────────────────────────────────────────────────
  'usage.title': '使用情况',
  'usage.subtitle': '查看请求量、Token 用量和费用变化。',
  'usage.range.aria_label': '时间范围',
  'usage.range.24h': '24 小时',
  'usage.range.7d': '7 天',
  'usage.range.30d': '30 天',
  'usage.daily.title': '每日 Tokens · {label}',
  'usage.daily.subtitle': '帮助你观察最近一段时间的整体 Token 趋势。',
  'usage.daily.loading': '正在加载每日用量…',
  'usage.daily.error': '无法加载每日用量',
  'usage.daily.empty': '最近 {label} 暂无用量',
  'usage.legend.input': '输入 Tokens',
  'usage.legend.output': '输出 Tokens',
  'usage.summary.requests': '请求数',
  'usage.summary.input_tokens': '输入 Tokens',
  'usage.summary.output_tokens': '输出 Tokens',
  'usage.summary.cost': '费用',
  'usage.summary.range': '统计范围：{label}',
  'usage.summary.loading': '加载中…',
  'usage.summary.cost_unit': '按美元计',
  'usage.summary.cache_read_suffix': '+ {tokens} 缓存读取 Token',
  'usage.summary.cache_write_suffix': '+ {tokens} 缓存写入 Token',
  'usage.technical.summary': '查看缓存命中和缓存写入带来的额外 Token 统计。',
  'usage.technical.cache_read': '缓存命中 Token',
  'usage.technical.cache_write': '缓存写入 Token',
  'usage.chart.title': '请求量变化 · {label}',
  'usage.chart.loading': '正在加载图表…',
  'usage.chart.error': '无法加载图表',
  'usage.by_key.title': '按密钥查看 · {label}',
  'usage.by_key.loading': '正在加载分解…',
  'usage.by_key.error': '无法加载分解',
  'usage.by_key.empty': '此时间范围暂无用量。',
  'usage.by_key.unnamed_key': '未命名密钥',
  'usage.by_key.unknown_key': '未知密钥',
  'usage.by_key.column.key_name': '密钥名称',
  'usage.by_key.column.requests': '请求数',
  'usage.by_key.column.input_tokens': '输入 Tokens',
  'usage.by_key.column.output_tokens': '输出 Tokens',
  'usage.by_key.column.cache_read': '缓存命中',
  'usage.by_key.column.cache_write': '缓存写入',
  'usage.by_key.column.cost': '费用',

  // ── Request logs page ─────────────────────────────────────────
  'requests.title': '请求日志',
  'requests.subtitle': '逐条查看请求状态、模型、Token、缓存和费用。',
  'requests.range.aria_label': '请求日志时间范围',
  'requests.range.24h': '24 小时',
  'requests.range.7d': '7 天',
  'requests.range.30d': '30 天',
  'requests.filter.request_id': '请求 ID',
  'requests.filter.request_id_placeholder': '精确搜索 request id',
  'requests.filter.key': '密钥',
  'requests.filter.key_all': '全部密钥',
  'requests.filter.status': '状态',
  'requests.filter.status_all': '全部状态',
  'requests.filter.status_success': '成功',
  'requests.filter.status_error': '错误',
  'requests.filter.submit': '搜索',
  'requests.filter.clear': '清除',
  'requests.key.unnamed': '未命名密钥',
  'requests.key.unknown': '未知密钥',
  'requests.loading': '正在加载请求日志…',
  'requests.error': '无法加载请求日志。',
  'requests.empty': '此筛选条件下暂无请求记录。',
  'requests.column.time': '时间',
  'requests.column.request': '请求 ID',
  'requests.column.key': '密钥名称',
  'requests.column.model': '模型',
  'requests.column.status': '状态',
  'requests.column.duration': '耗时',
  'requests.column.input': '输入',
  'requests.column.output': '输出',
  'requests.column.cache_read': '缓存命中',
  'requests.column.cache_write': '缓存写入',
  'requests.column.cost': '费用',
  'requests.duration.ms': '{value} ms',
  'requests.detail.request_id': '完整请求 ID',
  'requests.detail.api_key_id': '密钥 ID',
  'requests.detail.protocol': '协议',
  'requests.pagination.summary': '{from}–{to} / 共 {total}',
  'requests.pagination.prev': '较新',
  'requests.pagination.next': '较旧',

  // ── Pricing page ──────────────────────────────────────────────
  'pricing.title': '模型定价',
  'pricing.subtitle': '按厂商、渠道和模型查看每百万 Tokens 的有效价格。',
  'pricing.unit': '每百万 Tokens · USD',
  'pricing.loading': '正在加载模型定价…',
  'pricing.error': '无法加载模型定价。',
  'pricing.empty': '暂时还没有可显示的模型价格。',
  'pricing.vendor.summary': '{models} 个模型 · {channels} 个渠道',
  'pricing.vendor.no_channels': '此厂商当前没有可用渠道。',
  'pricing.column.model': '模型',
  'pricing.cell.unavailable': '不可用',
  'pricing.rate.input': '输入',
  'pricing.rate.output': '输出',
  'pricing.rate.cache_read': '缓存命中',
  'pricing.rate.cache_write': '缓存写入',

  // ── Billing page ───────────────────────────────────────────────
  'billing.title': '余额与账单',
  'billing.subtitle': '查看余额、充值、兑换记录和账单流水。',
  'billing.balance.title': '当前余额',
  'billing.balance.status.funded': '余额充足',
  'billing.balance.status.zero': '余额不足',
  'billing.balance.loading': '正在加载余额…',
  'billing.balance.error': '无法加载余额。',
  'billing.balance.updated': '更新于 {timestamp}',
  'billing.topup.entry': '充值',
  'billing.redeem.title': '兑换礼品卡',
  'billing.redeem.code_label': '礼品卡兑换码',
  'billing.redeem.code_placeholder': '32 位十六进制码',
  'billing.redeem.submit': '兑换',
  'billing.redeem.submitting': '兑换中…',
  'billing.redeem.error.code_required': '请输入礼品卡兑换码',
  'billing.redeem.toast.success': '已到账 {amount},当前余额 {balance}',
  'billing.transactions.title': '余额记录',
  'billing.transactions.latest': '最新',
  'billing.transactions.offset': '从第 {offset} 条开始',
  'billing.transactions.page': '第 {page} 页',
  'billing.transactions.loading': '正在加载交易…',
  'billing.transactions.error': '无法加载交易。',
  'billing.transactions.empty': '暂无交易。',
  'billing.transactions.column.date': '时间',
  'billing.transactions.column.type': '类型',
  'billing.transactions.column.amount_usd': '金额(USD)',
  'billing.transactions.column.running': '余额',
  'billing.transactions.newer': '较新',
  'billing.transactions.older': '较旧',
  'billing.transactions.type.charge': '请求费用',
  'billing.transactions.type.gift_card_redeem': '礼品卡入账',
  'billing.transactions.type.ops_adjust': '人工调整',
  'billing.transactions.type.topup': '充值入账',
  'billing.pricing.title': '模型定价',
  'billing.pricing.unit': '每百万 Tokens · USD',
  'billing.pricing.loading': '正在加载定价…',
  'billing.pricing.error': '无法加载定价表。',
  'billing.pricing.empty': '暂时还没有可显示的模型价格。',
  'billing.pricing.column.vendor': '提供方',
  'billing.pricing.column.model': '模型',
  'billing.pricing.column.input': '输入',
  'billing.pricing.column.output': '输出',
  'billing.pricing.column.cache_read': '缓存命中',
  'billing.pricing.column.cache_write': '缓存写入',
  'billing.pricing.column.effective_from': '生效时间',

  // ── Billing topup page ─────────────────────────────────────────
  'topup.title': '充值',
  'topup.subtitle': '使用微信支付为余额充值。下一步会显示二维码，你可以直接用手机扫码完成支付。',
  'topup.amount.label': '金额(CNY · 整元)',
  'topup.amount.hint': 'CNY · 最小 {min} · 最大 {max} · 仅整元',
  'topup.usd.label': '将获得的余额',
  'topup.usd.hint': '按约 {rate} ¥/$ 估算；实际到账金额以下单时的汇率为准。',
  'topup.usd.hint_loading': '正在读取当前服务端报价。',
  'topup.usd.hint_unavailable': '暂时无法读取报价；实际到账金额会在下单时由服务端锁定。',
  'topup.pay_type.legend': '支付方式',
  'topup.pay_type.alipay': '支付宝',
  'topup.pay_type.wxpay': '微信支付',
  'topup.error.whole_yuan': '请输入整元金额(整数,不含小数)。',
  'topup.error.positive': '请输入正的整元金额。',
  'topup.error.required': '请输入金额以继续。',
  'topup.error.no_cents': '金额必须为整元(不能含分)。',
  'topup.error.min': '最小充值为 {amount}。',
  'topup.error.max': '最大充值为 {amount}。',
  'topup.error.no_redirect': '支付通道未返回跳转地址。',
  'topup.cancel': '取消',
  'topup.submit': '前往支付 →',
  'topup.submitting': '创建中…',
  'topup.fee_hint': '汇率会定期刷新，支付通道手续费由平台承担。',
  'topup.qr.title': '扫码支付',
  'topup.qr.order_prefix': '订单 ',
  'topup.qr.image_alt': '支付二维码',
  'topup.qr.fallback_hint': '在手机上打开微信 App,扫描下方目标 URL。',
  'topup.qr.status.paid': '已收到支付,正在跳转…',
  'topup.qr.status.other': '订单 {status}',
  'topup.qr.status.pending': '等待支付…',
  'topup.qr.status.slow': '用时超过预期…',
  'topup.qr.slow_notice':
    '为节省带宽我们已停止轮询。若你已在 App 中完成支付,余额会自行更新,可安全关闭此弹窗。',
  'topup.qr.close': '关闭',

  // ── Settings page ──────────────────────────────────────────────
  'settings.title': '账号与团队',
  'settings.subtitle': '管理账号、团队成员和安全设置。',
  'settings.account.title': '账号信息',
  'settings.account.description': '这里展示你当前账号最常用的信息。',
  'settings.account.email': '邮箱',
  'settings.account.account_id': '账户 ID',
  'settings.account.org_id': '团队 ID',
  'settings.account.email_verified': '邮箱验证状态',
  'settings.account.status.verified': '已验证',
  'settings.account.status.unverified': '未验证',
  'settings.technical.summary': '原始 ID 和其他技术字段会放在这里，便于排障时查看。',
  'settings.members.title': '团队成员',
  'settings.members.subtitle': '可访问当前团队的人员列表。管理员和所有者可以邀请新成员。',
  'settings.members.invite_button': '邀请成员',
  'settings.members.loading': '正在加载成员…',
  'settings.members.error': '无法加载成员',
  'settings.members.empty': '暂无成员。',
  'settings.members.column.email': '邮箱',
  'settings.members.column.role': '角色',
  'settings.members.column.joined': '加入时间',
  'settings.members.role.owner': '所有者',
  'settings.members.role.admin': '管理员',
  'settings.members.role.member': '成员',
  'settings.invitations.title': '待处理邀请',
  'settings.invitations.loading': '加载中…',
  'settings.invitations.error': '无法加载邀请',
  'settings.invitations.empty': '暂无待处理邀请。',
  'settings.invitations.column.email': '邮箱',
  'settings.invitations.column.role': '角色',
  'settings.invitations.column.invited_by': '邀请人',
  'settings.invitations.column.expires': '过期',
  'settings.invitations.column.actions': '操作',
  'settings.invitations.revoke': '撤销',
  'settings.invite_modal.title': '邀请新成员',
  'settings.invite_modal.description':
    '受邀者会收到一封邀请邮件，也可以复制邀请链接手动发送给对方。',
  'settings.invite_modal.email': '邮箱',
  'settings.invite_modal.role': '角色',
  'settings.invite_modal.role.member': '成员',
  'settings.invite_modal.role.admin': '管理员',
  'settings.invite_modal.email_required': '请填写邮箱',
  'settings.invite_modal.cancel': '取消',
  'settings.invite_modal.submit': '发送邀请',
  'settings.invite_modal.submitting': '发送中…',
  'settings.invite_modal.toast.sent': '邀请已发送',
  'settings.invite_link.title': '邀请已发送',
  'settings.invite_link.description': '收件人会收到带下方链接的邮件。你也可以复制并手动分享。',
  'settings.invite_link.click_to_copy': '点击复制',
  'settings.invite_link.copied': '已复制到剪贴板',
  'settings.invite_link.copy_anywhere': '点击此区块任意位置复制',
  'settings.invite_link.done': '完成',
  'settings.invite_link.aria_label': '复制邀请链接到剪贴板',
  'settings.security.title': '安全',
  'settings.security.change_password': '修改登录密码',
  'settings.security.current_password': '当前密码',
  'settings.security.new_password': '新密码(10–128 位)',
  'settings.security.confirm_password': '确认新密码',
  'settings.security.submit': '修改密码',
  'settings.security.submitting': '保存中…',
  'settings.security.error.current_required': '请填写当前密码',
  'settings.security.error.current_incorrect': '当前密码不正确',
  'settings.security.error.password_too_short': '密码长度至少 {min} 位',
  'settings.security.error.password_too_long': '密码长度最多 {max} 位',
  'settings.security.error.mismatch': '两次输入的密码不一致',
  'settings.security.error.network': '网络错误——请重试',
  'settings.security.toast.success': '密码已修改',

  // ── i18n switcher chrome ───────────────────────────────────────
  // The aria-label on the button group IS translated so screen
  // readers announce it in the active locale rather than always
  // hearing "Language" regardless of UI language.
  'i18n.switcher.aria_label': '语言',

  // ── Toast ───────────────────────────────────────────────────────
  // Only the aria-labels are translated; toast bodies come from the
  // call site (already localised per context or surfaced as raw
  // server error messages).
  'toast.region_aria': '通知',
  'toast.dismiss_aria': '关闭通知',

  // ── Billing topup return ────────────────────────────────────────
  // Rendered at `/billing/topup/return` after a ZPay provider redirect.
  // Every card variant (pending / slow / paid / expired / failed /
  // refunded / missing-order) has its own headline + body so screen
  // readers and sighted users get a consistent narrative per state.
  'billing.topup_return.page.title': '充值结果',
  'billing.topup_return.page.order_label': '订单 ',
  'billing.topup_return.page.waiting_order_id': '等待订单号…',
  'billing.topup_return.loading.aria': '正在加载订单状态…',
  'billing.topup_return.missing.title': '缺少订单号',
  'billing.topup_return.missing.body':
    '此页面通常由支付服务商的回跳链接进入。如果你是误入,返回账务总览即可。',
  'billing.topup_return.missing.cta': '返回账务',
  'billing.topup_return.pending.title': '支付进行中',
  'billing.topup_return.pending.body': '支付正在处理。确认到账后本页会自动刷新——无需手动重载。',
  'billing.topup_return.slow.title': '支付确认比平常慢',
  'billing.topup_return.slow.body':
    '服务商尚未确认你的支付。如果已在 App 内完成支付,几分钟后余额会自动更新——可以安全关闭此页。',
  'billing.topup_return.slow.reference': '参考订单号:',
  'billing.topup_return.slow.cta.back': '返回账务',
  'billing.topup_return.slow.cta.support': '联系支持',
  'billing.topup_return.paid.title': '支付成功',
  'billing.topup_return.paid.credited': '到账',
  'billing.topup_return.paid.charged': '实付',
  'billing.topup_return.paid.at_rate': '汇率 {rate} ¥/$',
  'billing.topup_return.paid.new_balance': '新余额',
  'billing.topup_return.paid.balance_loading': '加载中…',
  'billing.topup_return.paid.redirect_hint': '数秒后将跳转至账务总览…',
  'billing.topup_return.paid.cta': '返回仪表盘',
  'billing.topup_return.terminal.expired.title': '订单已过期',
  'billing.topup_return.terminal.expired.body': '此订单在完成支付前已过期。如仍需充值请重新发起。',
  'billing.topup_return.terminal.failed.title': '支付失败',
  'billing.topup_return.terminal.failed.body':
    '支付服务商报告此订单失败。未实际扣款,可以安全重试。',
  'billing.topup_return.terminal.refunded.title': '订单已退款',
  'billing.topup_return.terminal.refunded.body': '此订单已被退款。如仍需补充余额请重新发起充值。',
  'billing.topup_return.terminal.cta.retry': '重试',
  'billing.topup_return.terminal.cta.back': '返回账务',

  // ── Usage charts ────────────────────────────────────────────────
  // `usage_chart.*` powers both UsageChart (hourly) + DailyUsageChart.
  // Each chart has its own aria-summary + per-bar <title> so screen
  // readers hear the shape of the data, not just the empty state.
  'usage_chart.empty': '此时间范围暂无用量数据',
  'usage_chart.hourly.aria': '每小时用量——{count} 个时段,峰值 {peak} 次请求',
  'usage_chart.hourly.tooltip': '{hour} —— {requests} 次请求, {tokens} tokens',
  'usage_chart.daily.aria':
    '每日 Token 用量——{days} 天,共 {total} tokens(输入 {input} / 输出 {output})',
  'usage_chart.daily.tooltip':
    '{date} —— {total} tokens(输入 {input} + 输出 {output}),{requests} 次请求',

  // ── Skeleton ────────────────────────────────────────────────────
  'skeleton.loading': '加载中…',

  // ── Error banner ────────────────────────────────────────────────
  'error_banner.unexpected': '意外错误',
} as const;
